import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Send, CheckCircle, AlertCircle, MessageSquare,
  Bug, Lightbulb, Heart, Star
} from 'lucide-react';
import { apiEndpoints } from '../config/api';

const FeedbackPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    type: 'general',
    rating: 0,
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const feedbackTypes = [
    { id: 'general', label: 'General Feedback', icon: MessageSquare, color: 'cyclara' },
    { id: 'bug', label: 'Bug Report', icon: Bug, color: 'red' },
    { id: 'feature', label: 'Feature Request', icon: Lightbulb, color: 'amber' },
    { id: 'love', label: 'Share Some Love', icon: Heart, color: 'pink' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(apiEndpoints.feedback, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', type: 'general', rating: 0, message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <main className="min-h-screen pt-32 pb-24 px-6 md:px-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-cyclara-100 text-cyclara-600 
                        text-sm font-medium mb-4">
            We'd Love to Hear From You
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-midnight-500 mb-4">
            Share Your <span className="gradient-text">Feedback</span>
          </h1>
          <p className="text-xl text-midnight-400 max-w-2xl mx-auto">
            Your feedback helps us make Cyclara better for everyone. 
            Whether it's a bug, a feature idea, or just to say hi — we're listening!
          </p>
        </motion.div>

        {/* Success Message */}
        {submitStatus === 'success' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-8 p-6 bg-green-50 border border-green-200 rounded-2xl flex items-center gap-4"
          >
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-green-800">Thank you for your feedback!</h3>
              <p className="text-green-600">We appreciate you taking the time to help us improve Cyclara.</p>
            </div>
          </motion.div>
        )}

        {/* Error Message */}
        {submitStatus === 'error' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mb-8 p-6 bg-red-50 border border-red-200 rounded-2xl flex items-center gap-4"
          >
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold text-red-800">Something went wrong</h3>
              <p className="text-red-600">Please try again or email us directly at feedback@cyclara.app</p>
            </div>
          </motion.div>
        )}

        {/* Feedback Form */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-3xl shadow-xl shadow-midnight-500/5 border border-cyclara-100 p-8 md:p-12"
        >
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Feedback Type */}
            <div>
              <label className="block text-sm font-semibold text-midnight-500 mb-4">
                What type of feedback is this?
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {feedbackTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, type: type.id }))}
                    className={`p-4 rounded-2xl border-2 transition-all duration-300 ${
                      formData.type === type.id
                        ? 'border-cyclara-500 bg-cyclara-50'
                        : 'border-cyclara-100 hover:border-cyclara-300'
                    }`}
                  >
                    <type.icon className={`w-6 h-6 mx-auto mb-2 ${
                      formData.type === type.id ? 'text-cyclara-500' : 'text-midnight-400'
                    }`} />
                    <span className={`text-sm font-medium ${
                      formData.type === type.id ? 'text-cyclara-600' : 'text-midnight-500'
                    }`}>
                      {type.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Name & Email */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-midnight-500 mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Jane Doe"
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-midnight-500 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="jane@example.com"
                  required
                />
              </div>
            </div>

            {/* Rating */}
            <div>
              <label className="block text-sm font-semibold text-midnight-500 mb-4">
                How would you rate your experience? (Optional)
              </label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, rating: star }))}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-8 h-8 transition-colors ${
                        star <= formData.rating
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-midnight-200 hover:text-yellow-300'
                      }`}
                    />
                  </button>
                ))}
                {formData.rating > 0 && (
                  <span className="ml-4 text-midnight-400">
                    {formData.rating === 5 ? '🎉 Awesome!' : 
                     formData.rating >= 4 ? '😊 Great!' :
                     formData.rating >= 3 ? '🙂 Good' :
                     formData.rating >= 2 ? '😐 Okay' : '😕 Needs work'}
                  </span>
                )}
              </div>
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-midnight-500 mb-2">
                Your Feedback
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Tell us what's on your mind..."
                rows={6}
                required
                className="resize-none"
              />
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full py-4 bg-gradient-to-r from-cyclara-500 to-lavender-500 text-white 
                       font-semibold rounded-2xl shadow-lg shadow-cyclara-500/25
                       hover:shadow-xl hover:shadow-cyclara-500/30 transition-all
                       disabled:opacity-50 disabled:cursor-not-allowed
                       flex items-center justify-center gap-3"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  <span>Send Feedback</span>
                </>
              )}
            </motion.button>
          </form>
        </motion.div>

        {/* Alternative Contact */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-12 text-center"
        >
          <p className="text-midnight-400">
            Prefer email? Reach us directly at{' '}
            <a href="mailto:feedback@cyclara.app" className="text-cyclara-500 hover:underline font-medium">
              feedback@cyclara.app
            </a>
          </p>
        </motion.div>
      </div>
    </main>
  );
};

export default FeedbackPage;
